import React, { useEffect, useState } from "react";
import {
  Box,
  Badge,
  Button,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  Radio,
  RadioGroup,
  Select,
  SimpleGrid,
  Spinner,
  Text,
  Textarea,
  VStack,
  Circle,
  useToast,
} from "@chakra-ui/react";
import {
  MdArrowBack,
  MdArrowForward,
  MdCalendarToday,
  MdInfo,
  MdPhone,
  MdPerson,
} from "react-icons/md";
import { useNavigate, useParams } from "react-router-dom";
import apiClient from "../../../api/client";
import { BASE_URL } from "config/api";

const decisionOptions = [
  {
    id: "accepter",
    title: "SMUR — Service Mobile Urgence Réanimation",
    desc: "Équipe médicalisée (médecin ou infirmier anesthésiste)",
    icon: "🚑",
  },
  {
    id: "avis",
    title: "Avis",
    desc: "Avis médical seul, sans transport",
    icon: "💬",
  },
  {
    id: "refus",
    title: "Refus",
    desc: "Transport non indiqué",
    icon: "⛔",
  },
];

export default function DossierRegulationDecision() {
  const navigate = useNavigate();
  const { id } = useParams();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [dossier, setDossier] = useState(null);
  const [patient, setPatient] = useState(null);
  const [usersById, setUsersById] = useState({});
  const [hospitalVillesList, setHospitalVillesList] = useState([]);
  const [hospitals, setHospitals] = useState([]);
  const [medecinsHopitalDestination, setMedecinsHopitalDestination] = useState([]);

  const [form, setForm] = useState({
    interrogatoire: "",
    hypothese: "",
    conduite: "",
    contre_indications: "",
    etat_mission: "",
    date_mission: "",
    villeDestination: "",
    hopitalDestination: "",
    serviceDestination: "",
    medecinReceveur: "",
    decision: "",
    motif_de_cloture: "",
  });

  const displayValue = (value) => {
    if (value === null || value === undefined || value === "") return "---";
    return String(value);
  };

  const formatDate = (value) => {
    if (!value) return "---";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);
    return date.toLocaleString("fr-FR");
  };

  const formatSexe = (sexe) => {
    const s = String(sexe || "").trim().toLowerCase();
    if (["m", "male", "homme", "h"].includes(s)) return "Homme";
    if (["f", "female", "femme"].includes(s)) return "Femme";
    return displayValue(sexe);
  };

  const formatOuiNon = (value) => {
    if (value === 1 || value === "1" || value === true) return "Oui";
    if (value === 0 || value === "0" || value === false) return "Non";
    return "---";
  };

  const getUserFullName = (userId, fallback = "Médecin") => {
    if (!userId) return "---";
    const user = usersById[String(userId)];
    if (user) {
      const fullName = `${user.nom || ""} ${user.prenom || ""}`.trim();
      if (fullName) return fullName;
    }
    return `${fallback} #${userId}`;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toNull = (value) => {
    if (value === null || value === undefined) return null;
    if (typeof value === "string" && value.trim() === "") return null;
    return value;
  };

  const normalizeDateMissionValue = (value) => {
    const raw = String(value || "").trim();
    if (!raw) return null;
    return raw.slice(0, 16);
  };

  const getNowLocalDateTimeValue = () => {
    const now = new Date();
    const tzOffsetMs = now.getTimezoneOffset() * 60000;
    return new Date(now.getTime() - tzOffsetMs).toISOString().slice(0, 16);
  };

  useEffect(() => {
    const fetchDossier = async () => {
      try {
        setLoading(true);
        setError("");
        const res = await apiClient.get(`${BASE_URL}/dossier/${id}`);
        const data = res.data || null;
        setDossier(data);

        if (!data) {
          setError("Dossier introuvable.");
          return;
        }

        if (data.patient_id) {
          try {
            const patientRes = await apiClient.get(`${BASE_URL}/patients/${data.patient_id}`);
            setPatient(patientRes.data || null);
          } catch {
            setPatient(null);
          }
        }

        const doctorIds = [...new Set([data.doc_a, data.doc_b].filter(Boolean))];
        if (doctorIds.length > 0) {
          try {
            const responses = await Promise.all(
              doctorIds.map((doctorId) => apiClient.get(`${BASE_URL}/users/${doctorId}`))
            );
            const map = {};
            responses.forEach((res, idx) => {
              map[String(doctorIds[idx])] = res.data || null;
            });
            setUsersById(map);
          } catch {
            setUsersById({});
          }
        } else {
          setUsersById({});
        }

        setForm({
          interrogatoire: data.interrogatoire || "",
          hypothese: data.hypothese || "",
          conduite: data.conduite || "",
          contre_indications: data.contre_indications || "",
          etat_mission: data.etat_mission || "",
          date_mission: data.date_mission
            ? new Date(data.date_mission).toISOString().slice(0, 16)
            : "",
          villeDestination: data.ville_dest || "",
          hopitalDestination: data.hospital_destination_id ? String(data.hospital_destination_id) : "",
          serviceDestination: data.service_b || "",
          medecinReceveur: data.doc_b ? String(data.doc_b) : "",
          decision: (data.decision || "").toString().toLowerCase(),
          motif_de_cloture: data.motif_de_cloture || "",
        });
      } catch {
        setError("Impossible de charger le dossier.");
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchDossier();
  }, [id]);

  useEffect(() => {
    const fetchHopitaux = async () => {
      try {
        const [hopitalRes, villesRes] = await Promise.all([
          apiClient.get(`${BASE_URL}/hopitals`),
          apiClient.get(`${BASE_URL}/hopitals/villes`),
        ]);
        setHospitals(Array.isArray(hopitalRes.data) ? hopitalRes.data : []);
        const villesData = villesRes.data;
        const villesList = Array.isArray(villesData) ? villesData : villesData?.villes || [];
        setHospitalVillesList(Array.isArray(villesList) ? villesList : []);
      } catch {
        setHospitals([]);
        setHospitalVillesList([]);
      }
    };
    fetchHopitaux();
  }, []);

  useEffect(() => {
    const fetchMedecinsDestination = async () => {
      if (!form.hopitalDestination) {
        setMedecinsHopitalDestination([]);
        return;
      }
      try {
        const res = await apiClient.get(`${BASE_URL}/users/hopital/${form.hopitalDestination}/medecins`);
        setMedecinsHopitalDestination(Array.isArray(res.data) ? res.data : []);
      } catch {
        setMedecinsHopitalDestination([]);
      }
    };
    fetchMedecinsDestination();
  }, [form.hopitalDestination]);

  useEffect(() => {
    if (form.decision === "accepter" && !form.date_mission) {
      setForm((prev) => ({ ...prev, date_mission: getNowLocalDateTimeValue() }));
    }
  }, [form.decision, form.date_mission]);

  const saveDecision = async () => {
    if (!form.decision) {
      toast({
        title: "Décision requise",
        description: "Choisissez une décision de régulation.",
        status: "warning",
        isClosable: true,
      });
      return;
    }

    if (form.decision === "refus" && !String(form.motif_de_cloture || "").trim()) {
      toast({
        title: "Motif requis",
        description: "Le motif de clôture est obligatoire pour un refus.",
        status: "warning",
        isClosable: true,
      });
      return;
    }

    const payload = {
      interrogatoire: toNull(form.interrogatoire),
      hypothese: toNull(form.hypothese),
      conduite: toNull(form.conduite),
      contre_indications: toNull(form.contre_indications),
      motif_de_cloture: toNull(form.motif_de_cloture),
      decision: toNull(form.decision),
      statut: form.decision === "accepter" ? "attente affectation" : "termine",
    };

    if (form.decision === "accepter") {
      payload.date_mission = normalizeDateMissionValue(form.date_mission);
      payload.etat_mission = toNull(form.etat_mission);
    }

    const selectedHopitalDestination = hospitals.find(
      (h) => String(h.id) === String(form.hopitalDestination)
    );
    const receveurPayload = {
      ville_dest: toNull(form.villeDestination),
      hospital_destination_id: toNull(form.hopitalDestination ? Number(form.hopitalDestination) : null),
      adresse_destination: toNull(selectedHopitalDestination?.nom || dossier?.adresse_destination),
      service_b: toNull(form.serviceDestination),
      doc_b: toNull(form.medecinReceveur ? Number(form.medecinReceveur) : null),
    };

    const shouldUpdateReceveur =
      form.decision === "accepter" &&
      (receveurPayload.ville_dest !== null ||
        receveurPayload.hospital_destination_id !== null ||
        receveurPayload.service_b !== null ||
        receveurPayload.doc_b !== null);

    try {
      setSaving(true);
      if (shouldUpdateReceveur) {
        try {
          await apiClient.patch(`${BASE_URL}/dossier/${id}`, receveurPayload);
        } catch {
          await apiClient.put(`${BASE_URL}/dossier/${id}`, { ...dossier, ...receveurPayload });
        }
      }
      await apiClient.put(`${BASE_URL}/dossier/${id}/decision`, payload);

      toast({
        title: "Décision enregistrée",
        status: "success",
        isClosable: true,
      });
      navigate(`/admin/dossier-regulation/${id}`);
    } catch (e) {
      const serverMessage = e?.response?.data?.error || e?.response?.data?.message || null;
      toast({
        title: "Erreur",
        description: serverMessage || "Impossible d'enregistrer la décision.",
        status: "error",
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box p="20px" pt="120px" bg="#F7F8FA" minH="100vh">
        <Flex minH="280px" align="center" justify="center">
          <Spinner />
        </Flex>
      </Box>
    );
  }

  if (error || !dossier) {
    return (
      <Box p="20px" pt="120px" bg="#F7F8FA" minH="100vh">
        <Box bg="white" borderRadius="10px" border="1px solid #E2E8F0" p="20px">
          <Text color="red.500" mb="12px">{error || "Dossier introuvable."}</Text>
          <Button onClick={() => navigate("/admin/dossier-regulation")} variant="outline">
            Retour à la liste
          </Button>
        </Box>
      </Box>
    );
  }

  const patientData = patient || dossier?.patient || null;
  const patientName = patientData
    ? `${patientData.nom || ""} ${patientData.prenom || ""}`.trim() || `Patient #${dossier.patient_id}`
    : dossier?.patient_id
      ? `Patient #${dossier.patient_id}`
      : "---";

  return (
    <Box p="20px" pt="120px" bg="#F7F8FA" minH="100vh">
      <HStack spacing="10px" mb="16px" align="center">
        <IconButton
          icon={<Icon as={MdArrowBack} />}
          variant="ghost"
          size="sm"
          onClick={() => navigate(`/admin/dossier-regulation/${id}`)}
          aria-label="Retour"
        />
        <Text fontSize="14px" color="#656C7A" cursor="pointer" onClick={() => navigate("/admin/dossier-regulation")}>
          Dossier de régulation
        </Text>
        <Icon as={MdArrowForward} w="12px" h="12px" color="#656C7A" transform="rotate(-90deg)" />
        <Text fontSize="14px" color="#455879" fontWeight="600">Décision</Text>
      </HStack>

      <Box bg="white" border="1px solid #E2E8F0" borderRadius="12px" p="16px" mb="16px">
        <Flex justify="space-between" align="start" wrap="wrap" gap="12px">
          <HStack spacing="14px" align="start">
            <Box w="64px" h="64px" borderRadius="10px" bg="#EAF4FF" display="flex" alignItems="center" justifyContent="center">
              <Icon as={MdInfo} color="#2B6CF0" boxSize="30px" />
            </Box>
            <VStack align="start" spacing="2px">
              <Text fontSize="18px" fontWeight="700" color="#1B2559">
                Dossier #{displayValue(dossier.reference || `REG-${String(dossier.id).padStart(4, "0")}`)}
              </Text>
              <HStack spacing="12px" wrap="wrap">
                <HStack spacing="4px">
                  <Icon as={MdCalendarToday} color="#8A94A6" boxSize="14px" />
                  <Text fontSize="13px" color="#656C7A">Créé le {formatDate(dossier.créé_le || dossier.created_at)}</Text>
                </HStack>
                <HStack spacing="4px">
                  <Icon as={MdPhone} color="#8A94A6" boxSize="14px" />
                  <Text fontSize="13px" color="#656C7A">{displayValue(dossier.phone)}</Text>
                </HStack>
              </HStack>
            </VStack>
          </HStack>
          <HStack spacing="8px">
            <Badge bg="#F3F4F6" color="#455879" borderRadius="full" px="10px" py="6px">
              {displayValue(dossier.cause).toUpperCase()}
            </Badge>
            <Badge bg="#E2F0FF" color="#1D4ED8" borderRadius="full" px="10px" py="6px">
              {displayValue(dossier.statut)}
            </Badge>
          </HStack>
        </Flex>
      </Box>

      <Flex gap="20px" align="flex-start" direction={{ base: "column", xl: "row" }}>
        <Box w={{ base: "100%", xl: "360px" }}>
          <VStack spacing="16px" align="stretch">
            <Box bg="white" borderRadius="12px" border="1px solid #E2E8F0" overflow="hidden">
              <Box px="18px" py="12px" borderBottom="1px solid" borderColor="gray.100">
                <Text fontSize="16px" fontWeight="600" color="#455879">Informations patient</Text>
              </Box>
              <Box p="16px">
                <HStack align="flex-start" spacing="10px" mb="14px">
                  <Box
                    minW="36px"
                    h="36px"
                    borderRadius="8px"
                    border="1px solid"
                    borderColor="#FECACA"
                    bg="#FFF5F5"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Icon as={MdPerson} color="#DC2626" boxSize="18px" />
                  </Box>
                  <VStack align="flex-start" spacing="1px">
                    <Text fontSize="14px" color="#656C7A">Patient</Text>
                    <Text fontSize="20px" fontWeight="700" color="#111827" lineHeight="1.2">
                      {patientName}
                    </Text>
                  </VStack>
                </HStack>

                <VStack align="stretch" spacing="0">
                  <HStack justify="space-between" py="8px">
                    <Text fontSize="14px" color="#656C7A">Âge / Sexe</Text>
                    <Text fontSize="14px" color="#111827" fontWeight="500">
                      {displayValue(patientData?.age)} ans / {formatSexe(patientData?.sexe)}
                    </Text>
                  </HStack>
                  <Divider />
                  <HStack justify="space-between" py="8px">
                    <Text fontSize="14px" color="#656C7A">IPP</Text>
                    <Text fontSize="14px" color="#111827" fontWeight="500">
                      {displayValue(patientData?.ipp || dossier.ipp)}
                    </Text>
                  </HStack>
                  <Divider />
                  <HStack justify="space-between" py="8px">
                    <Text fontSize="14px" color="#656C7A">Téléphone</Text>
                    <Text fontSize="14px" color="#111827" fontWeight="500">
                      {displayValue(patientData?.téléphone || patientData?.telephone || dossier.phone)}
                    </Text>
                  </HStack>
                </VStack>
              </Box>
            </Box>

            <Box bg="white" borderRadius="12px" border="1px solid #E2E8F0" overflow="hidden">
              <Box px="18px" py="12px" borderBottom="1px solid" borderColor="gray.100">
                <Text fontSize="16px" fontWeight="600" color="#455879">Signes Vitaux </Text>
              </Box>
              <Box p="16px">
                
                <Text fontSize="14px" color="#656C7A" mb="12px">Signes Vitaux</Text>

                <SimpleGrid columns={{ base: 1, md: 2, xl: 2 }} spacing="10px" mb="12px">
                  <Box bg="#F3F4F6" borderRadius="8px" p="10px 12px">
                    <Text fontSize="13px" fontWeight="600" color="#667085" mb="8px">Respire</Text>
                    <Text fontSize="19px" lineHeight="1" fontWeight="700" color="#111827">
                      {formatOuiNon(dossier.respire)}
                    </Text>
                  </Box>

                  <Box bg="#F3F4F6" borderRadius="8px" p="10px 12px">
                    <Text fontSize="13px" fontWeight="600" color="#667085" mb="8px">Conscient</Text>
                    <Text fontSize="19px" lineHeight="1" fontWeight="700" color="#111827">
                      {formatOuiNon(dossier.conscient)}
                    </Text>
                  </Box>

                  <Box bg="#F3F4F6" borderRadius="8px" p="10px 12px">
                    <Text fontSize="13px" fontWeight="600" color="#667085" textTransform="lowercase" mb="8px">spo2</Text>
                    <HStack align="baseline" spacing="6px">
                      <Text fontSize="19px" lineHeight="1" fontWeight="700" color="#111827">{displayValue(dossier.spo2)}</Text>
                      <Text fontSize="10px" lineHeight="1" color="#111827">%</Text>
                      <Text fontSize="9px" letterSpacing="0.4px" color="#111827">CRITICAL</Text>
                    </HStack>
                  </Box>

                  <Box bg="#F3F4F6" borderRadius="8px" p="10px 12px">
                    <Text fontSize="13px" fontWeight="600" color="#667085" textTransform="lowercase" mb="8px">
                      score de glasgow
                    </Text>
                    <HStack align="baseline" spacing="6px">
                      <Text fontSize="19px" lineHeight="1" fontWeight="700" color="#111827">{displayValue(dossier.glasgow)}</Text>
                      <Text fontSize="10px" lineHeight="1" color="#111827">/ 15</Text>
                      <Text fontSize="9px" color="#667085">E3 V4 M6</Text>
                    </HStack>
                  </Box>

                  <Box bg="#F3F4F6" borderRadius="8px" p="10px 12px">
                    <Text fontSize="13px" fontWeight="600" color="#667085" textTransform="uppercase" mb="8px">
                      BP (SYS/DIA)
                    </Text>
                    <HStack align="baseline" spacing="8px">
                      <Text fontSize="19px" lineHeight="1" fontWeight="700" color="#111827">{displayValue(dossier.ta)}</Text>
                      <Text fontSize="10px" color="#667085">mmHg</Text>
                    </HStack>
                  </Box>

                  <Box bg="#F3F4F6" borderRadius="8px" p="10px 12px">
                    <Text fontSize="13px" fontWeight="600" color="#667085" textTransform="lowercase" mb="8px">
                      fréquence cardiaque
                    </Text>
                    <HStack align="baseline" spacing="8px">
                      <Text fontSize="19px" lineHeight="1" fontWeight="700" color="#111827">{displayValue(dossier.fc)}</Text>
                      <Text fontSize="10px" color="#667085">bpm</Text>
                    </HStack>
                  </Box>

                </SimpleGrid>

                <Box>
                  <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="8px">Autres constantes / Remarques</Text>
                  <Text fontSize="14px" color="#455879">
                    {displayValue(dossier.autre) !== "---" ? displayValue(dossier.autre) : displayValue(dossier.note)}
                  </Text>
                </Box>
              </Box>
            </Box>

            <Box bg="white" borderRadius="12px" border="1px solid #E2E8F0" overflow="hidden">
              <Box px="18px" py="12px" borderBottom="1px solid" borderColor="gray.100">
                <Text fontSize="16px" fontWeight="600" color="#455879">Transport</Text>
              </Box>
              <Box p="16px">
                <VStack align="stretch" spacing="14px">
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px">
                    <Box>
                      <Text fontSize="12px" color="#656C7A" fontWeight="600">Type de transport</Text>
                      <Text fontSize="14px" color="#455879">{displayValue(dossier.transportType)}</Text>
                    </Box>
                  </SimpleGrid>

                  <Divider />

                  <Box>
                    <Text fontSize="13px" fontWeight="700" color="#455879" mb="8px">
                      Demandeur
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px">
                      <Box>
                        <Text fontSize="12px" color="#656C7A" fontWeight="600">
                          {String(dossier.transportType || "").toLowerCase() === "primaire"
                            ? "Adresse mission"
                            : "Hôpital demandeur"}
                        </Text>
                        <Text fontSize="14px" color="#455879">{displayValue(dossier.adresse)}</Text>
                      </Box>

                      {String(dossier.transportType || "").toLowerCase() !== "primaire" ? (
                        <>
                          <Box>
                            <Text fontSize="12px" color="#656C7A" fontWeight="600">Service demandeur</Text>
                            <Text fontSize="14px" color="#455879">{displayValue(dossier.service_a)}</Text>
                          </Box>
                          <Box>
                            <Text fontSize="12px" color="#656C7A" fontWeight="600">Médecin demandeur</Text>
                            <Text fontSize="14px" color="#455879">{getUserFullName(dossier.doc_a)}</Text>
                          </Box>
                        </>
                      ) : null}
                    </SimpleGrid>
                  </Box>

                  <Divider />

                  <Box>
                    <Text fontSize="13px" fontWeight="700" color="#455879" mb="8px">
                      Receveur
                    </Text>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing="12px">
                      <Box>
                        <Text fontSize="12px" color="#656C7A" fontWeight="600">Hôpital receveur</Text>
                        <Text fontSize="14px" color="#455879">{displayValue(dossier.adresse_destination)}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="12px" color="#656C7A" fontWeight="600">Service receveur</Text>
                        <Text fontSize="14px" color="#455879">{displayValue(dossier.service_b)}</Text>
                      </Box>
                      <Box>
                        <Text fontSize="12px" color="#656C7A" fontWeight="600">Médecin receveur</Text>
                        <Text fontSize="14px" color="#455879">{getUserFullName(dossier.doc_b)}</Text>
                      </Box>
                    </SimpleGrid>
                  </Box>
                </VStack>
              </Box>
            </Box>
          </VStack>
        </Box>

        <Box flex="1" minW={{ base: "100%", xl: "620px" }}>
          <VStack spacing="16px" align="stretch">
            <Box bg="white" borderRadius="12px" border="1px solid #E2E8F0" overflow="hidden">
              <Box px="18px" py="12px" borderBottom="1px solid" borderColor="gray.100">
                <Text fontSize="16px" fontWeight="600" color="#455879">Compléments cliniques - Bilan médecin</Text>
              </Box>
              <Box p="16px">
                <VStack align="stretch" spacing="14px">
                  <Box>
                    <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="8px">Interrogatoire médical</Text>
                    <Textarea minH="74px" value={form.interrogatoire} onChange={(e) => handleChange("interrogatoire", e.target.value)} />
                  </Box>
                  <Box>
                    <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="8px">Hypothèse diagnostique</Text>
                    <Textarea minH="74px" value={form.hypothese} onChange={(e) => handleChange("hypothese", e.target.value)} />
                  </Box>
                </VStack>
              </Box>
            </Box>

            <Box bg="white" borderRadius="12px" border="1px solid #E2E8F0" overflow="hidden">
              <Box px="18px" py="12px" borderBottom="1px solid" borderColor="gray.100">
                <Text fontSize="16px" fontWeight="600" color="#455879">Conduite à tenir médicale</Text>
              </Box>
              <Box p="16px">
                <VStack align="stretch" spacing="14px">
                  <Box>
                    <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="8px">Conduite à tenir</Text>
                    <Textarea minH="74px" value={form.conduite} onChange={(e) => handleChange("conduite", e.target.value)} />
                  </Box>
                  <Box>
                    <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="8px">Contre-indications au transport</Text>
                    <Textarea minH="74px" value={form.contre_indications} onChange={(e) => handleChange("contre_indications", e.target.value)} />
                  </Box>
                </VStack>
              </Box>
            </Box>
          </VStack>
        </Box>

        <Box w={{ base: "100%", xl: "520px" }} position={{ base: "static", xl: "sticky" }} top={{ xl: "220px" }}>
          <Box bg="white" borderRadius="12px" border="1px solid #E2E8F0" p="12px">
            <Text fontSize="16px" fontWeight="600" color="#455879" mb="12px">Décision de régulation</Text>
            <VStack align="stretch" spacing="10px" mb="12px">
              {decisionOptions.map((option) => {
                const selected = form.decision === option.id;
                return (
                  <Box
                    key={option.id}
                    border="1px solid"
                    borderColor={selected ? "#2B6CF0" : "gray.200"}
                    borderRadius="10px"
                    p="10px 12px"
                    bg={selected ? "#F5F9FF" : "white"}
                    cursor="pointer"
                    onClick={() => handleChange("decision", option.id)}
                  >
                    <HStack justify="space-between" align="flex-start" spacing="10px">
                      <HStack align="flex-start" spacing="10px">
                        <Box minW="40px" h="40px" borderRadius="8px" border="1px solid" borderColor="gray.200" display="flex" alignItems="center" justifyContent="center" fontSize="18px">
                          {option.icon}
                        </Box>
                        <VStack align="flex-start" spacing="2px">
                          <Text fontSize="14px" fontWeight="700" color="#455879" lineHeight="1.2">{option.title}</Text>
                          <Text fontSize="13px" color="#656C7A" lineHeight="1.2">{option.desc}</Text>
                        </VStack>
                      </HStack>
                      <Circle size="18px" border="2px solid" borderColor={selected ? "#2B6CF0" : "gray.300"} bg={selected ? "#2B6CF0" : "transparent"}>
                        {selected ? <Circle size="7px" bg="white" /> : null}
                      </Circle>
                    </HStack>
                  </Box>
                );
              })}
            </VStack>

            {form.decision === "accepter" && (
              <>
                <Box mb="12px">
                  <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="8px">État mission</Text>
                  <RadioGroup
                    value={form.etat_mission}
                    onChange={(value) => handleChange("etat_mission", value)}
                  >
                    <HStack spacing="20px" align="center">
                      <Radio value="planifiée">
                      planifiée
                      </Radio>
                      <Radio value="Aller-Retour">
                      Aller-Retour
                      </Radio>
                    </HStack>
                  </RadioGroup>
                </Box>

                <Box mb="12px">
                  <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="8px">Date mission</Text>
                  <Input
                    type="datetime-local"
                    value={form.date_mission}
                    onChange={(e) => handleChange("date_mission", e.target.value)}
                  />
                </Box>

                <Box mb="12px">
                  <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="8px">Ville destination</Text>
                  <Select
                    size="sm"
                    value={form.villeDestination}
                    onChange={(e) => {
                      handleChange("villeDestination", e.target.value);
                      handleChange("hopitalDestination", "");
                      handleChange("medecinReceveur", "");
                    }}
                  >
                    <option value="">Sélectionner...</option>
                    {hospitalVillesList.map((ville) => (
                      <option key={ville} value={ville}>{ville}</option>
                    ))}
                  </Select>
                </Box>

                <Box mb="12px">
                  <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="8px">Hôpital receveur</Text>
                  <Select
                    size="sm"
                    value={form.hopitalDestination}
                    onChange={(e) => {
                      handleChange("hopitalDestination", e.target.value);
                      handleChange("medecinReceveur", "");
                    }}
                  >
                    <option value="">Sélectionner...</option>
                    {hospitals
                      .filter((h) => !form.villeDestination || String(h.ville || "").trim() === String(form.villeDestination || "").trim())
                      .map((hospital) => (
                        <option key={hospital.id} value={hospital.id}>{hospital.nom}</option>
                      ))}
                  </Select>
                </Box>

                <Box mb="12px">
                  <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="8px">Service receveur</Text>
                  <Input
                    size="sm"
                    value={form.serviceDestination}
                    onChange={(e) => handleChange("serviceDestination", e.target.value)}
                    placeholder="Saisissez le service..."
                  />
                </Box>

                <Box mb="12px">
                  <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="8px">Médecin receveur</Text>
                  <Select
                    size="sm"
                    value={form.medecinReceveur}
                    onChange={(e) => handleChange("medecinReceveur", e.target.value)}
                  >
                    <option value="">Sélectionner...</option>
                    {medecinsHopitalDestination.map((doctor) => (
                      <option key={doctor.id} value={doctor.id}>
                        {`${doctor.nom || ""} ${doctor.prenom || ""}`.trim() || `Médecin #${doctor.id}`}
                      </option>
                    ))}
                  </Select>
                </Box>
              </>
            )}

            <Box mb="12px">
              <Text fontSize="13px" color="#656C7A" fontWeight="600" mb="8px">Motif de clôture</Text>
              <Textarea
                minH="90px"
                value={form.motif_de_cloture}
                onChange={(e) => handleChange("motif_de_cloture", e.target.value)}
                placeholder="Justification médicale du refus ou avis sans transport..."
              />
            </Box>

            <HStack justify="space-between">
              <Button variant="outline" onClick={() => navigate(`/admin/dossier-regulation/${id}`)}>
                Annuler
              </Button>
              <Button colorScheme="blue" onClick={saveDecision} isLoading={saving}>
                Valider décision
              </Button>
            </HStack>
          </Box>
        </Box>
      </Flex>
    </Box>
  );
}
